import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CtaBand } from "@/components/CtaBand";
import { PageHero } from "@/components/PageHero";
import { PillLink } from "@/components/PillLink";
import { Seo } from "@/components/Seo";
import { useReveals } from "@/lib/motion";
import { DOCS_URL, SIGNUP_URL } from "@/lib/site";
import { FEATURES, LEGACY_FEATURE_REDIRECTS } from "./content";

export default function FeaturePage() {
  const { slug = "" } = useParams();
  const scope = useReveals();

  const index = FEATURES.findIndex((feature) => feature.slug === slug);
  if (index === -1) return <Navigate to={LEGACY_FEATURE_REDIRECTS[slug] ?? "/"} replace />;

  const feature = FEATURES[index];
  const next = FEATURES[(index + 1) % FEATURES.length];

  return (
    <div ref={scope}>
      <Seo title={feature.name} description={feature.description} path={`/features/${feature.slug}`} />

      <PageHero eyebrow={feature.name} title={feature.title} lede={feature.lede}>
        <PillLink to={SIGNUP_URL} label="Start free" variant="accent" arrow />
        <PillLink to={`${DOCS_URL}${feature.docsPath}`} label="Read the guide" variant="outline" />
      </PageHero>

      <section data-theme="light" className="bg-bg">
        <div className="shell py-24 md:py-36">
          <div className="flex items-baseline justify-between gap-6">
            <p data-fade className="eyebrow text-mute">
              What it does
            </p>
            <p data-fade className="eyebrow text-mute">
              {feature.plan}
            </p>
          </div>

          <ol className="mt-8">
            {feature.blocks.map((block, blockIndex) => (
              <li key={block.title}>
                <span data-rule className="rule" />
                <div data-fade className="grid gap-6 py-10 md:grid-cols-[5rem_minmax(0,1fr)_minmax(0,1.2fr)] md:gap-8 md:py-14">
                  <span className="eyebrow pt-2 text-mute">{String(blockIndex + 1).padStart(2, "0")}</span>
                  <h2 className="subtitle uppercase">{block.title}</h2>
                  <div>
                    <p className="lede max-w-[54ch]">{block.body}</p>
                    <ul className="mt-6 space-y-2.5">
                      {block.points.map((point) => (
                        <li key={point} className="flex gap-3 text-mute">
                          <span className="mt-[0.7em] h-px w-4 shrink-0 bg-accent" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <span data-rule className="rule" />
        </div>
      </section>

      <section data-theme="dark" className="bg-bg">
        <div className="shell grid gap-10 py-24 md:py-36 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <p data-fade className="eyebrow eyebrow-mark mb-6 text-mute">
              Good to know
            </p>
            <h2 data-split className="title max-w-[10ch]">
              Where it stops today.
            </h2>
          </div>
          <ul className="self-end">
            {feature.limits.map((limit) => (
              <li key={limit}>
                <span data-rule className="rule" />
                <p data-fade className="lede max-w-[58ch] py-6 text-fg/80">
                  {limit}
                </p>
              </li>
            ))}
            <li aria-hidden="true">
              <span data-rule className="rule" />
            </li>
          </ul>
        </div>
      </section>

      <section data-theme="light" className="bg-bg">
        <Link to={`/features/${next.slug}`} className="group block">
          <div className="shell flex items-end justify-between gap-8 py-20 md:py-28">
            <div>
              <p className="eyebrow mb-5 text-mute">Next</p>
              <p className="title transition-transform duration-700 ease-out-expo group-hover:translate-x-3">{next.name}</p>
            </div>
            <ArrowUpRight
              aria-hidden="true"
              className="h-12 w-12 shrink-0 transition-transform duration-700 ease-out-expo group-hover:-translate-y-2 group-hover:translate-x-2 md:h-24 md:w-24"
              strokeWidth={1}
            />
          </div>
        </Link>
      </section>

      <CtaBand />
    </div>
  );
}
